import * as React from "react";

import "./kiwis-status.scss";

import {Feed, Icon, Progress, Segment} from "semantic-ui-react";
import {observer} from "mobx-react";
import {Kiwis, StatusType} from "../../../kiwis";
import {action, computed} from "mobx";


export interface KiwisStatusProps {
    kiwis: Kiwis;
}

enum KiwisStatusWindowMode {
    Compact = "compact",
    Normal = "normal",
    Extended = "extended"
}

/**
 * 0: compact > normal
 * 1: normal > extended
 * 2: extended > normal
 * 3: normal > compact
 * 4: compact > extended
 * 5: extended > compact
 */
enum KiwisStatusWindowTransitionMode {
    CompactToNormal = 0,
    NormalToExtended = 1,
    ExtendedToNormal = 2,
    NormalToCompact = 3,
    CompactToExtended = 4,
    ExtendedToCompact = 5
}

@observer
export class KiwisStatus extends React.Component<KiwisStatusProps, any> {
    disposeModeToggleTimer: any;
    constructor(props: KiwisStatusProps) {
        super(props);
        this.state = {
            mode: KiwisStatusWindowMode.Normal,
            modeTransition: KiwisStatusWindowTransitionMode.CompactToNormal
        };
        this.toggleMode = this.toggleMode.bind(this);
    }

    render() {
        // clear old timers
        clearTimeout(this.disposeModeToggleTimer);

        const status = this.props.kiwis.status;
        let content;
        let progress;
        if (this.state.mode === "compact") {
            content = <Icon className="geo-kiwis-status-icon" name="inbox" size="large" />;
        } else {
            // TODO: es sollen nur die neuen Events zu einem rerender führen.
            //  Nur der aktuellste Status soll gezeigt werden
            // Es sollen aber alle Events angezeigt werden können, wenn das Fenster vergrössert wird.
            // Fenster soll geschlossen werden, wenn für 30 sek keine Meldung rein kommt
            const feedEvents = status.map((item, i) => {
                return (
                    <Feed.Event
                        key={i}
                        icon={this.iconByType(item.type)}
                        summary={item.message}
                        className={i === status.length - 1 ? "current" : "past"}
                    />
                );
            });
            content = <Feed>{feedEvents}</Feed>;

            progress = (<Progress
                percent={this.currentStatusType === StatusType.Complete ? 100 : 50}
                attached="bottom"
                active={this.currentStatusType === StatusType.Loading}
                warning={this.currentStatusType === StatusType.Slowly}
                error={this.currentStatusType === StatusType.Error}
                success={this.currentStatusType === StatusType.Complete}
            />);

            // set timer to compact the window
            this.disposeModeToggleTimer = setTimeout(() => {
                clearTimeout(this.disposeModeToggleTimer);
                if (this.currentStatusType === StatusType.Complete) {
                    // collapse if last status has type 'complete'
                    this.setMode(KiwisStatusWindowMode.Compact);
                }
            }, 10000);
        }

        return (
            <Segment size={"small"}
                className={`geo-component geo-kiwis-status geo-widget geo-popup--shadow ${this.state.mode}`}
                onClick={this.toggleMode}>
                <div className={"geo-kiwis-status-content"}>{content}</div>
                {progress}
            </Segment>
        );
    }

    componentDidMount(): void {}

    componentWillUnmount(): void {
        clearTimeout(this.disposeModeToggleTimer);
    }

    @action
    setMode(newMode: KiwisStatusWindowMode) {
        if (newMode === this.state.mode) {
            // do not change
            return;
        }
        if (newMode === KiwisStatusWindowMode.Compact) {
            if (this.currentStatusType !== StatusType.Complete) {
                // do not switch to compact mode when type of current message is not complete
                return;
            }
        }
        this.setState((state: any) => {
            let modeTransition;
            if (newMode === KiwisStatusWindowMode.Compact) {
                modeTransition = KiwisStatusWindowTransitionMode.NormalToCompact;
            }
            if (state.mode === KiwisStatusWindowMode.Normal) {
                if (state.modeTransition === KiwisStatusWindowTransitionMode.NormalToExtended) {
                    modeTransition = KiwisStatusWindowTransitionMode.ExtendedToNormal;
                }
                if (state.modeTransition === KiwisStatusWindowTransitionMode.NormalToCompact) {
                    modeTransition = KiwisStatusWindowTransitionMode.CompactToNormal;
                }
            }
            if (newMode === KiwisStatusWindowMode.Extended) {
                modeTransition = KiwisStatusWindowTransitionMode.NormalToExtended;
            }
            return { mode: newMode, modeTransition };
        });
    }

    @computed
    get currentStatusType() {
        return this.props.kiwis.status[this.props.kiwis.status.length - 1].type;
    }

    /**
     * Toggles mode between compact > normal > extended > normal > compact
     * depending the modeTransition
     */
    toggleMode() {
        this.setState((state: any) => {
            let mode;
            let modeTransition;
            if (state.mode === KiwisStatusWindowMode.Compact) {
                mode = KiwisStatusWindowMode.Extended;
                modeTransition = KiwisStatusWindowTransitionMode.CompactToExtended;
            }
            if (state.mode === KiwisStatusWindowMode.Normal) {
                mode = KiwisStatusWindowMode.Extended;
                modeTransition = KiwisStatusWindowTransitionMode.NormalToExtended;
            }
            if (state.mode === KiwisStatusWindowMode.Extended) {
                if (state.modeTransition === KiwisStatusWindowTransitionMode.CompactToExtended) {
                    mode = KiwisStatusWindowMode.Compact;
                    modeTransition = KiwisStatusWindowTransitionMode.ExtendedToCompact;
                }
                if (state.modeTransition === KiwisStatusWindowTransitionMode.NormalToExtended) {
                    mode = KiwisStatusWindowMode.Normal;
                    modeTransition = KiwisStatusWindowTransitionMode.ExtendedToNormal;
                }
            }
            return { mode, modeTransition };
        });
    }

    // return appropriate icon per type
    private iconByType(type: StatusType) {
        if (type === StatusType.Loading) {
            return "sync alternate";
        }
        if (type === StatusType.Slowly) {
            return "hourglass start";
        }
        if (type === StatusType.Complete) {
            return "check";
        }
        if (type === StatusType.Error) {
            return "bug";
        }
    }
}
